import { useMutation, useQuery } from '@tanstack/react-query';
import { JSONContent } from '@tiptap/react';
import { resourcesAPI } from 'api/resources';
import clsx from 'clsx';
import LoadingSpinner from 'components/loader/LoadingSpinner';
import Button from 'components/utils/Button';
import Input from 'components/utils/Input';
import { ModuleContext } from 'components/utils/module/ModuleContext';
import { AvoSlideCloseModal } from 'components/utils/panels/AvoSlideCloseModal';
import RequiredMark from 'components/utils/requiredMark';
import { Skeleton } from 'components/utils/Skeleton';
import { CustomToast } from 'components/utils/toast-message';
import { Body2, Caption2, H4 } from 'components/utils/typo';
import { X } from 'lucide-react';
import { useContext, useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { MODULE_TYPES } from '../../../../constants';
import { generateID } from '../../../../utils/utilityFunctions';
import { ConditionalTextConditions } from './ConditionalTextConditions';
import { isEmpty } from 'lodash';
import { JsonGroup } from '@react-awesome-query-builder/antd';

interface ConditionalTextFormProps {
  conditionalTextId?: number;
  onSave: (item) => void;
  onCancel: () => void;
}

export interface Condition {
  id: number | string;
  is_default: boolean;
  label: string;
  position: number;
  text: string;
  text_content: string;
  // Advanced phenotyping has a condition of null
  condition: string | null;
  condition_logic: Record<string, unknown>;
  builder_condition_json?: JsonGroup;
  text_content_tiptap: JSONContent;
}

export interface ConditionalTextFormVariable {
  name: string;
  conditions: Condition[];
}

export const additionalCondition: Condition = {
  id: generateID(),
  is_default: false,
  label: 'Condition 1',
  position: 0,
  text: '',
  text_content: '',
  condition: '',
  condition_logic: {},
  builder_condition_json: undefined,
  text_content_tiptap: {},
};

export const defaultCondition: Condition = {
  id: generateID(),
  is_default: true,
  label: 'None of the above (Default)',
  position: 1,
  text: '',
  text_content: '',
  condition: '',
  condition_logic: {},
  builder_condition_json: undefined,
  text_content_tiptap: {},
};

export const ConditionalTextForm = ({
  conditionalTextId,
  onSave,
  onCancel,
}: ConditionalTextFormProps) => {
  const { module } = useContext(ModuleContext);
  const [closeModalOpen, setCloseModalOpen] = useState(false);

  const {
    data: conditionalText,
    isLoading,
    error,
  } = useQuery<ConditionalTextFormVariable, any>({
    queryKey: ['conditionalText', conditionalTextId],
    queryFn: resourcesAPI.getConditionalText,
    enabled: !!conditionalTextId,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: (failureCount, error) => {
      if (error.status === 404) return false;
      return true;
    },
  });

  useEffect(() => {
    if (!conditionalTextId) return;
    if (error && error.status === 404) {
      toast.error(CustomToast, {
        data: 'This conditional text is no longer exist.',
      });
      onCancel();
    }
  }, [conditionalTextId, conditionalText, error]);

  const { isPending, mutateAsync } = useMutation({
    mutationFn: !conditionalTextId
      ? resourcesAPI.postConditionalText
      : (data: ConditionalTextFormVariable) =>
          resourcesAPI.patchConditionalText(conditionalTextId, data),
  });

  const defaultValues = {
    name: '',
    conditions: [additionalCondition, defaultCondition],
    calculator: module?.type === MODULE_TYPES.CALCULATOR ? module.id : '',
    module: module?.type === MODULE_TYPES.ALGO ? module.id : '',
  };

  const form = useForm<ConditionalTextFormVariable>({ defaultValues });
  const {
    watch,
    register,
    reset,
    handleSubmit,
    setError,
    formState: { isDirty, errors },
  } = form;

  useEffect(() => {
    if (!conditionalText) return;
    reset({
      name: conditionalText.name,
      conditions: conditionalText.conditions,
    });
  }, [conditionalText]);

  const onSubmit = async (data: ConditionalTextFormVariable) => {
    if (isPending) return;
    let hasErrors = false;
    const conditions = watch('conditions');
    // Some items use only condition_logic without condition.
    // Treat as non-empty if either condition or condition_logic exists.
    // TODO: Added for user-facing condition visibility; may need rewrite if string logic is introduced.
    if (
      conditions.some(
        (condition) =>
          condition.is_default === false &&
          (!condition.label ||
            !condition.text ||
            (!condition.condition && isEmpty(condition.condition_logic)))
      )
    ) {
      setError('conditions', {
        type: 'required',
        message: 'Please fill all required field in each options.',
      });
      hasErrors = true;
    }
    // Some items use only condition_logic without condition.
    // Treat as non-empty if either condition or condition_logic exists.
    // TODO: Added for user-facing condition visibility; may need rewrite if string logic is introduced.
    const conditionSet = new Set(
      conditions.map(
        (condition) => condition.condition || JSON.stringify(condition.condition_logic)
      )
    );
    if (conditionSet.size !== conditions.length) {
      setError('conditions', {
        type: 'validate',
        message: 'Options with same or blank condition are not allowed.',
      });
      hasErrors = true;
    }
    if (hasErrors) return;

    const res = await mutateAsync(data);
    onSave({ ...res, type: 'conditional_text' });
  };
  return (
    <>
      <FormProvider {...form}>
        <form
          className='flex h-full flex-col'
          onSubmit={handleSubmit((data, e) => {
            e?.stopPropagation();
            onSubmit(data);
          })}
        >
          <div className='grow overflow-y-auto'>
            <div className='sticky top-0 z-10 flex items-center justify-between bg-white pl-4 pr-3 pt-[20px]'>
              <H4>Conditional Text</H4>
              <div
                className='cursor-pointer'
                onClick={() => (isDirty ? setCloseModalOpen(true) : onCancel())}
              >
                <X size={20} />
              </div>
            </div>
            <div className='mt-4 px-4'>
              <div className='space-y-1'>
                <div className='flex gap-1'>
                  <Body2 className='text-gray-900'>Name</Body2>
                  <RequiredMark />
                </div>
                {isLoading ? (
                  <Skeleton />
                ) : (
                  <>
                    <Input
                      {...register('name', { required: true, maxLength: 255 })}
                      required
                      maxLength={255}
                    />
                    <Caption2 className='flex justify-end text-gray-text-weak'>
                      {watch('name').length}/255
                    </Caption2>
                  </>
                )}
              </div>
              <div className='mt-6'>
                <div
                  className={clsx(
                    'rounded',
                    errors.conditions &&
                      'border-red-500 ring-1 !ring-red-500' /* TODO: Need to remove ring after remove global style*/
                  )}
                >
                  <ConditionalTextConditions />
                </div>
                {errors.conditions && (
                  <span className='text-error'>{errors.conditions.message}</span>
                )}
              </div>
            </div>
          </div>
          <div className='shrink-0'>
            <div className='flex justify-end gap-2 px-3 pb-[22px] pt-3'>
              <div className='flex'>
                <Button.Reverse
                  type='button'
                  onClick={() => (isDirty ? setCloseModalOpen(true) : onCancel())}
                  disabled={isPending}
                >
                  Cancel
                </Button.Reverse>
              </div>
              <div className='flex w-[80px]'>
                <Button
                  disabled={isPending}
                  type='button'
                  onClick={
                    isDirty
                      ? handleSubmit((data, e) => {
                          e?.stopPropagation();
                          onSubmit(data);
                        })
                      : onCancel
                  }
                >
                  {isPending ? <LoadingSpinner /> : 'Save'}
                </Button>
              </div>
            </div>
          </div>
        </form>
      </FormProvider>
      <AvoSlideCloseModal
        open={closeModalOpen}
        onContinue={() => setCloseModalOpen(false)}
        onDiscard={onCancel}
      />
    </>
  );
};
